import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'linear-gradient(135deg, #FAFAFA 0%, #FFF7ED 100%)'}}>
      <Card className="w-full max-w-md bg-white border-[#E5E5E5] shadow-2xl" data-testid="forgot-password-card">
        <CardHeader className="space-y-2">
          <CardTitle className="text-3xl font-heading font-bold text-[#0A0A0A]">Recuperar contraseña</CardTitle>
          <CardDescription className="text-[#737373]">
            Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {submitted ? (
            <div className="space-y-4 text-center py-4" data-testid="forgot-success">
              <CheckCircle2 className="w-16 h-16 text-primary mx-auto" />
              <h3 className="text-lg font-semibold text-[#0A0A0A]">¡Revisa tu email!</h3>
              <p className="text-sm text-[#737373]">
                Si <strong className="text-[#0A0A0A]">{email}</strong> está registrado, recibirás un enlace para restablecer tu contraseña en los próximos minutos. El enlace expira en 1 hora.
              </p>
              <p className="text-xs text-[#737373]">
                ¿No lo encontrás? Revisá tu carpeta de spam.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full mt-4 border-[#E5E5E5] text-[#0A0A0A] hover:bg-[#F5F5F5]" data-testid="back-to-login-button">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#0A0A0A]">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 bg-white border-[#E5E5E5] text-[#0A0A0A] focus:border-primary"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3" data-testid="forgot-error-message">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold"
                data-testid="send-reset-link-button"
              >
                {loading ? 'Enviando...' : 'Enviar enlace de recuperación'}
              </Button>
              <Link to="/login" className="block text-center text-sm text-[#737373] hover:text-primary transition-colors" data-testid="back-to-login-link">
                <ArrowLeft className="w-4 h-4 inline mr-1" />
                Volver al login
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
